// useSyncQueueProcessor.js
import { useCallback, useEffect, useRef } from 'react'

import { useAuth } from 'src/auth'
import { logger } from 'src/lib/logger'
import type { NativeObjectType } from 'src/lib/objects'

import { getSyncQueueEntries, removeFromSyncQueue } from './searchIndex' // Adjust import path as needed

/**
 * Custom hook that watches and processes a sync queue
 *
 * @param {Object} options Configuration options
 * @param {string} options.workspaceId The workspace ID
 * @param {Function} options.processItems Function to process a batch of items (objectType, objectIds)
 * @param {Function} options.filterItems Function to filter items before processing (optional)
 * @param {Function} options.logInfo Logging function (optional)
 * @param {number} options.batchSize Number of items to process in a batch (default: 100)
 * @param {number} options.pollingInterval MS between queue checks (default: 100ms when active, 5000ms when idle)
 * @param {boolean} options.enabled Whether the processor is enabled (default: true)
 * @returns {Object} Queue processor state and controls
 */

const loggingEnabled = !process.env.HOST.includes('localhost')

const useSyncQueueProcessor = ({
  workspaceId,
  processItems,
  filterItems = (_objectType, objectIds) => objectIds,
  batchSize = 100,
  pollingInterval = { active: 100, idle: 5000 },
  enabled = true,
}) => {
  const { currentUser } = useAuth()

  // Use refs for stable values
  const workspaceIdRef = useRef(workspaceId)
  const userEmailRef = useRef(currentUser?.email)

  // Update refs when values change
  useEffect(() => {
    workspaceIdRef.current = workspaceId
  }, [workspaceId])

  useEffect(() => {
    userEmailRef.current = currentUser?.email
  }, [currentUser?.email])

  const processingRef = useRef(false)
  const timerRef = useRef(null)
  const statsRef = useRef({
    totalProcessed: 0,
    lastBatchSize: 0,
    lastProcessedType: null,
    isIdle: true, // Start idle, only go active when we have work
  })

  // Stable logInfo with no dependencies
  const logInfo = useCallback(
    (message: string, data: any, level: 'info' | 'warn' = 'info') => {
      if (loggingEnabled) {
        logger[level]({
          ...data,
          message,
          component: 'useSyncQueueProcessor',
          workspaceId: workspaceIdRef.current,
          userEmail: userEmailRef.current,
        })
      }
    },
    [] // No dependencies - uses refs instead
  )

  // Clear any existing timers when unmounting
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
      }
    }
  }, [])

  /**
   * Process a single batch from the queue
   */
  const processBatch = useCallback(async () => {
    if (processingRef.current || !enabled || !workspaceId) {
      return
    }

    try {
      processingRef.current = true

      // Fetch current queue entries
      const syncQueueEntries = await getSyncQueueEntries({ workspaceId })

      if (!syncQueueEntries || syncQueueEntries.length === 0) {
        // Queue is empty, update stats and return
        statsRef.current.isIdle = true
        return
      }

      // Group entries by type for batch processing (FIFO for types)
      const entriesByType = {}
      for (const entry of syncQueueEntries) {
        if (!entriesByType[entry.objectType]) {
          entriesByType[entry.objectType] = []
        }
        entriesByType[entry.objectType].push(entry.objectId)
      }

      // Get the first type with entries
      const objectTypes = Object.keys(entriesByType)
      if (objectTypes.length === 0) {
        statsRef.current.isIdle = true
        return
      }

      const objectType = objectTypes[0]
      const objectIdsToProcess = entriesByType[objectType] || []

      // Apply filtering (if any)
      const filteredIds = filterItems(objectType, objectIdsToProcess)

      // Handle excluded items
      const excludedIds = objectIdsToProcess.filter(
        (id) => !filteredIds.includes(id)
      )
      if (excludedIds.length > 0) {
        logInfo('Queue processor: Removing excluded items', {
          objectType,
          count: excludedIds.length,
        })

        await removeFromSyncQueue({
          workspaceId,
          objectType: objectType as NativeObjectType,
          objectIds: excludedIds,
        })
      }

      // Process a batch if there are filtered IDs
      if (filteredIds.length > 0) {
        const batchIds = filteredIds.slice(0, batchSize)

        logInfo('Queue processor: Processing batch', {
          objectType,
          batchCount: batchIds.length,
          totalForTypeInQueue: filteredIds.length,
        })

        // Process the batch using the provided processor function
        await processItems(objectType, batchIds)

        // Update stats
        statsRef.current = {
          ...statsRef.current,
          totalProcessed: statsRef.current.totalProcessed + batchIds.length,
          lastBatchSize: batchIds.length,
          lastProcessedType: objectType,
          isIdle: false,
        }
      } else if (objectIdsToProcess.length > 0) {
        // All items were filtered out
        logInfo('Queue processor: All items filtered out', {
          objectType,
          count: objectIdsToProcess.length,
        })

        // Remove from queue since they were all filtered
        await removeFromSyncQueue({
          workspaceId,
          objectType: objectType as NativeObjectType,
          objectIds: objectIdsToProcess,
        })
      }
    } catch (error) {
      logInfo(
        'Queue processor: Error processing batch',
        {
          error: error.message,
        },
        'warn'
      )
    } finally {
      processingRef.current = false

      // Schedule next processing cycle
      const nextInterval = statsRef.current.isIdle
        ? pollingInterval.idle
        : pollingInterval.active

      logInfo('Queue processor: Scheduling next cycle', {
        isIdle: statsRef.current.isIdle,
        nextInterval,
        activeInterval: pollingInterval.active,
        idleInterval: pollingInterval.idle,
        lastBatchSize: statsRef.current.lastBatchSize,
        totalProcessed: statsRef.current.totalProcessed,
      })

      timerRef.current = setTimeout(processBatch, nextInterval)
    }
  }, [
    workspaceId,
    processItems,
    filterItems,
    batchSize,
    pollingInterval,
    enabled,
    // logInfo removed - it's now stable with no dependencies
  ])

  useEffect(() => {
    if (enabled && workspaceId) {
      // Start the processing cycle
      processBatch()
    } else if (!enabled && timerRef.current) {
      // Stop the processing cycle
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
  }, [enabled, workspaceId, processBatch])

  return {
    stats: statsRef.current,
    isProcessing: processingRef.current,
    forceTrigger: processBatch,
  }
}

export default useSyncQueueProcessor
